import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { CatalogBatchDetailPage } from '~/features/catalog-operations/pages/catalog-batch-detail-page';
import type { BatchDetailTab } from '~/features/catalog-operations/types/catalog-operations.types';
import {
  MEMBERSHIP_CATEGORIES,
  MEMBERSHIP_GROUPS,
} from '~/features/catalog-operations/utils/insight-membership';
import { RequireChannelConnection } from '~/features/onboarding/components/require-channel-connection';

const searchSchema = z.object({
  tab: z
    .enum([
      'delivery-health',
      'performance-signals',
      'enrichment-coverage',
      'optimization-quality',
      // Batch Details Funnel; the page falls back to delivery-health when the gate is off.
      'run-details',
      // Old link to the batch's runs; they now live in Run Details.
      'runs',
    ])
    .catch('delivery-health'),
  // Batch retry deep links: `run` selects that run on the Runs tab (its products show beside
  // the run list); `cls` picks the selected run's class tab; `na=1` lands on the original
  // run's products (what Needs Attention links to).
  run: z.string().optional().catch(undefined),
  cls: z.enum(['ALL', 'FAILED', 'NOT_ATTEMPTED']).optional().catch(undefined),
  na: z.coerce.number().optional().catch(undefined),
  // Set when this job was opened from an experiment's "View Batch Details"
  // CTA rather than the catalog-operations list — `batchId` is then the
  // experiment id, and a not-found response redirects back with a toast
  // instead of showing the inline error state.
  source: z.enum(['experiment']).optional(),
  // Set on the way back from a product opened in the insight-membership
  // drawer — re-opens that drawer (row + chip). Cleared when it closes.
  membership: z.enum(MEMBERSHIP_CATEGORIES).optional().catch(undefined),
  membershipLabel: z.string().optional().catch(undefined),
  membershipGroup: z.enum(MEMBERSHIP_GROUPS).optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/_tenant/catalog-operations/$batchId')({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { batchId } = Route.useParams();
  const { tab, source, membership, membershipLabel, membershipGroup, na } = Route.useSearch();
  return (
    <RequireChannelConnection>
      <CatalogBatchDetailPage
        batchId={batchId}
        initialTab={(na === 1 ? 'run-details' : tab) as BatchDetailTab}
        {...(source ? { source } : {})}
        {...(membership && membershipLabel
          ? {
              restoreMembership: {
                category: membership,
                label: membershipLabel,
                group: membershipGroup,
              },
            }
          : {})}
      />
    </RequireChannelConnection>
  );
}
